import { ConfirmModal, Loading, Table, TableRowMenuCell } from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useMessage } from '@/providers';
import {
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
} from '@/utils';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import {
  useCosts,
  useCreateCost,
  useDeleteCost,
  useUpdateCost,
} from '../../hooks';
import { DEFAULT_PAGINATION_SETTING, fillZeroToCost } from '../../utils';
import { useDrawerState } from '../DrawerStateProvider';
import { CostFormDrawer } from './CostFormDrawer';

export const CostsPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const costs = useCosts({});
  const { mutateAsync: createMutateAsync } = useCreateCost();
  const { mutateAsync: updateMutateAsync } = useUpdateCost();
  const { mutateAsync: deleteMutateAsync } = useDeleteCost();
  const editPermission = useMasterTablePermission(true);
  const [costDelete, setCostDelete] = useState<string | null>(null);

  if (costs.isPending || !costs.data) {
    return <Loading />;
  }

  const selected = costs.data.find(
    (d) =>
      drawerState.open &&
      drawerState.type === 'cost' &&
      drawerState.isEditing &&
      d.id === drawerState.id,
  );

  return (
    <>
      <Table
        dataSource={costs.data}
        rowKey="id"
        columns={[
          {
            title: t('code'),
            key: 'code',
            dataIndex: 'code',
            width: TABLE_CODE_WIDTH,
            render: fillZeroToCost,
          },
          {
            title: t('name'),
            key: 'name',
            dataIndex: 'name',
          },
          {
            title: t('start-fiscal-year'),
            key: 'startYear',
            dataIndex: 'startYear',
            width: TABLE_YEAR_WIDTH,
          },
          {
            title: t('end-fiscal-year'),
            key: 'endYear',
            dataIndex: 'endYear',
            width: TABLE_YEAR_WIDTH,
          },
          {
            title: t('created-datetime'),
            key: 'createdAt',
            dataIndex: 'createdAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          {
            title: t('updated-datetime'),
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          ...(editPermission.data
            ? [
                {
                  title: '',
                  key: 'actions',
                  fixed: 'right' as const,
                  width: TABLE_ROW_MENU_WIDTH,
                  onCell: () => ({
                    style: { minWidth: TABLE_ROW_MENU_WIDTH },
                  }),
                  render: (_, record) => (
                    <TableRowMenuCell
                      onEdit={() => {
                        setDrawerState({
                          open: true,
                          type: 'cost',
                          isEditing: true,
                          id: record.id,
                        });
                      }}
                      items={[
                        {
                          key: 'delete',
                          label: t('delete'),
                          danger: true,
                          onClick: () => setCostDelete(record.id),
                        },
                      ]}
                    />
                  ),
                },
              ]
            : []),
        ]}
        pagination={DEFAULT_PAGINATION_SETTING}
      />

      <CostFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: {
                ...selected,
                period: [
                  selected?.startYear
                    ? dayjs().year(selected.startYear)
                    : undefined,
                  selected?.endYear
                    ? dayjs().year(selected.endYear)
                    : undefined,
                ],
              },
            }
          : { type: 'new' })}
        onSubmit={async (values) => {
          invariant(drawerState.open, t('unreachable-code'));

          drawerState.isEditing
            ? await updateMutateAsync({ ...values, id: drawerState.id })
            : await createMutateAsync(values);

          await Promise.all([
            await openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            await costs.refetch(),
            (async () => {
              setDrawerState({ open: false });
            })(),
          ]);
        }}
      />
      <ConfirmModal
        open={costDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(costDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(costDelete);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setCostDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setCostDelete(null)}
        skipCheck
      />
    </>
  );
};
